/**
 * Google Analytics 4 Event Tracking Utility
 * Handles consent checking and event tracking for RandomMockups
 */

/**
 * Track a custom event with GA4
 * @param {string} eventName - The event name
 * @param {Object} eventParams - Event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (window.gtag && typeof window.gtag === 'function') {
    // Check consent before tracking
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      try {
        const { analytics } = JSON.parse(consent);
        if (analytics) {
          window.gtag('event', eventName, eventParams);
          console.log('GA4 Event:', eventName, eventParams);
        }
      } catch (error) {
        console.warn('Error parsing cookie consent:', error);
      }
    } else {
      // If no consent stored, assume analytics is denied
      console.log('Analytics tracking skipped - no consent');
    }
  } else {
    console.warn('GA4 not loaded - event not tracked:', eventName);
  }
};

/**
 * Track e-commerce purchase events
 * @param {Object} transaction - Transaction details
 */
export const trackPurchase = (transaction) => {
  if (window.gtag && typeof window.gtag === 'function') {
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      try {
        const { analytics } = JSON.parse(consent);
        if (analytics) {
          window.gtag('event', 'purchase', transaction);
          console.log('GA4 Purchase:', transaction);
        }
      } catch (error) {
        console.warn('Error parsing cookie consent for purchase:', error);
      }
    }
  }
};

/**
 * Track page views with custom parameters
 * @param {string} pageName - Page name
 * @param {Object} customParams - Custom parameters
 */
export const trackPageView = (pageName, customParams = {}) => {
  if (window.gtag && typeof window.gtag === 'function') {
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      try {
        const { analytics } = JSON.parse(consent);
        if (analytics) {
          window.gtag('event', 'page_view', {
            page_title: pageName,
            page_location: window.location.href,
            ...customParams
          });
          console.log('GA4 Page View:', pageName, customParams);
        }
      } catch (error) {
        console.warn('Error parsing cookie consent for page view:', error);
      }
    }
  }
};

/**
 * Track user engagement events
 * @param {string} action - Action taken
 * @param {string} category - Event category
 * @param {Object} additionalParams - Additional parameters
 */
export const trackEngagement = (action, category = 'engagement', additionalParams = {}) => {
  trackEvent(action, {
    event_category: category,
    ...additionalParams
  });
};

/**
 * Track conversion events
 * @param {string} conversionType - Type of conversion
 * @param {Object} conversionData - Conversion data
 */
export const trackConversion = (conversionType, conversionData = {}) => {
  trackEvent('conversion', {
    conversion_type: conversionType,
    ...conversionData
  });
};

/**
 * Track error events
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message
 * @param {Object} additionalData - Additional error data
 */
export const trackError = (errorType, errorMessage, additionalData = {}) => {
  trackEvent('error', {
    error_type: errorType,
    error_message: errorMessage,
    ...additionalData
  });
};

/**
 * Track mockup generation events
 * @param {Object} mockupData - Mockup generation data
 */
export const trackMockupGeneration = (mockupData) => {
  trackEvent('generate_mockup', {
    event_category: 'mockup_generation',
    ...mockupData
  });
};

/**
 * Track pricing page interactions
 * @param {string} action - Action taken on pricing page
 * @param {Object} pricingData - Pricing-related data
 */
export const trackPricingInteraction = (action, pricingData = {}) => {
  trackEvent(action, {
    event_category: 'pricing',
    ...pricingData
  });
};

/**
 * Track file upload events
 * @param {Object} uploadData - Upload data
 */
export const trackFileUpload = (uploadData) => {
  trackEvent('upload_image', {
    event_category: 'file_upload',
    ...uploadData
  });
};

/**
 * Track CTA button clicks
 * @param {string} buttonLocation - Location of the button
 * @param {string} buttonText - Button text
 * @param {Object} additionalData - Additional data
 */
export const trackCTAClick = (buttonLocation, buttonText, additionalData = {}) => {
  trackEvent('cta_click', {
    event_category: 'engagement',
    button_location: buttonLocation,
    button_text: buttonText,
    ...additionalData
  });
};

/**
 * Track user journey events
 * @param {string} step - Journey step
 * @param {Object} journeyData - Journey data
 */
export const trackUserJourney = (step, journeyData = {}) => {
  trackEvent('user_journey', {
    event_category: 'user_flow',
    journey_step: step,
    ...journeyData
  });
};

/**
 * Check if analytics is enabled
 * @returns {boolean} - Whether analytics is enabled
 */
export const isAnalyticsEnabled = () => {
  const consent = localStorage.getItem('cookieConsent');
  if (consent) {
    try {
      const { analytics } = JSON.parse(consent);
      return analytics === true;
    } catch (error) {
      return false;
    }
  }
  return false;
};

/**
 * Track begin checkout event for e-commerce
 * @param {Object} checkoutData - Checkout data
 */
export const trackBeginCheckout = (checkoutData) => {
  if (window.gtag && typeof window.gtag === 'function') {
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      try {
        const { analytics } = JSON.parse(consent);
        if (analytics) {
          window.gtag('event', 'begin_checkout', checkoutData);
          console.log('GA4 Begin Checkout:', checkoutData);
        }
      } catch (error) {
        console.warn('Error parsing cookie consent for checkout:', error);
      }
    }
  }
};

/**
 * Set user properties for GA4
 * @param {Object} properties - User properties
 */
export const setUserProperties = (properties) => {
  if (window.gtag && typeof window.gtag === 'function') {
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      try {
        const { analytics } = JSON.parse(consent);
        if (analytics) {
          window.gtag('config', 'G-4SL5464RH1', {
            custom_map: properties
          });
          console.log('GA4 User Properties:', properties);
        }
      } catch (error) {
        console.warn('Error setting user properties:', error);
      }
    }
  }
};